/**
 * Opens a preview of one component of a set: photo, title, price, then the A+
 * content from custom.xenon_aciklama as you scroll.
 *
 * WHY A DELEGATED LISTENER AND NOT aria-controls.
 * ModalComponent binds its triggers ONCE, in connectedCallback, via
 * `document.querySelectorAll('[aria-controls=<id>]')`. Every trigger here lives
 * inside a product card, and assets/facets.js replaces #ProductGridContainer's
 * innerHTML wholesale on every filter, sort and load-more — so cards rendered
 * after the modal connected would never be bound. A document-level delegated
 * listener survives that, which is the same reasoning recorded at the top of
 * card-product-xenon.js.
 *
 * WHY THE CONTENT IS FETCHED AND NOT PRE-RENDERED.
 * The A+ block is 24-45 KB of HTML per product. A 24-card grid of 4-piece sets
 * would carry several megabytes of hidden markup. It is fetched on click through
 * the Section Rendering API instead, and cached per handle for the page's life.
 */
if (!customElements.get('set-item-modal')) {
  customElements.define(
    'set-item-modal',
    class SetItemModal extends DrawerComponent {
      constructor() {
        super();
        this.classesToRemoveOnLoad = 'drawer--loading';
        this.cache = new Map();
        this.handle = null;
      }

      get selector() {
        return '.set-item-modal__content';
      }

      get rail() {
        return this.querySelector('.set-item-modal__rail');
      }

      get foot() {
        return this.querySelector('.set-item-modal__foot');
      }

      /** The section wraps its markup in a <template> so the parsed document stays inert. */
      get sourceSelector() {
        return '#SetItemQuickview-content';
      }

      get requiresBodyAppended() {
        return true;
      }

      prepareToShow() {
        super.prepareToShow();
        this.load();
      }

      /**
       * Mirrors the card's own strip into the modal. The thumbnails are cloned
       * rather than re-fetched: they are already in the page and already in the
       * browser cache, so moving between components costs nothing.
       *
       * Cloning also decouples the rail from the grid. assets/facets.js replaces
       * #ProductGridContainer wholesale on filter and sort; the original strip can
       * vanish while the modal is open, and this copy keeps working.
       */
      buildChrome(strip) {
        const rail = this.rail;
        const foot = this.foot;
        if (!strip) {
          rail.innerHTML = '';
          rail.hidden = true;
          foot.hidden = true;
          return;
        }

        rail.innerHTML = '';
        strip.querySelectorAll('[data-set-item]').forEach((thumb) => {
          const copy = thumb.cloneNode(true);
          copy.classList.add('set-item-modal__rail-thumb');
          copy.setAttribute('role', 'tab');
          rail.appendChild(copy);
        });

        // A set of one has nothing to navigate between.
        rail.hidden = rail.children.length < 2;

        const setUrl = strip.dataset.setUrl;
        const cta = foot.querySelector('.set-item-modal__cta');
        if (setUrl) {
          cta.href = setUrl;
          const setTitle = strip.dataset.setTitle;
          if (setTitle) cta.setAttribute('aria-label', `${setTitle} — seti incele`);
          foot.hidden = false;
        } else {
          foot.hidden = true;
        }
      }

      /** Marks which component the body is currently showing. */
      syncRail() {
        this.rail.querySelectorAll('[data-set-item]').forEach((thumb) => {
          const active = thumb.dataset.setItem === this.handle;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-selected', active ? 'true' : 'false');
        });
      }

      handleAfterHide() {
        super.handleAfterHide();
        // Dropped rather than kept hidden: the A+ block ships its own <style> and
        // external stylesheets, and leaving several of them parked in the DOM lets
        // them bleed into the page behind the modal.
        this.querySelector(this.selector).innerHTML = '';
        this.rail.innerHTML = '';
        this.rail.hidden = true;
        this.foot.hidden = true;
        this.classList.add(this.classesToRemoveOnLoad);
      }

      async load() {
        const target = this.querySelector(this.selector);
        const handle = this.handle;
        if (!handle) return;

        try {
          let html = this.cache.get(handle);
          if (html === undefined) {
            const res = await fetch(
              `${window.Shopify.routes.root}products/${handle}?section_id=set-item-quickview`
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            html = await res.text();
            this.cache.set(handle, html);
          }

          // The modal can be closed, or another thumbnail clicked, while this is
          // in flight — do not paint a stale product into an open modal.
          if (this.handle !== handle) return;

          const tpl = new DOMParser().parseFromString(html, 'text/html').querySelector(this.sourceSelector);
          if (!tpl) throw new Error('set-item-quickview template missing');

          this.setInnerHTML(target, tpl.content.cloneNode(true));
          target.scrollTop = 0;
          this.syncRail();
          FoxTheme.a11y.trapFocus(this, this.focusElement);
          this.classList.remove(this.classesToRemoveOnLoad);
        } catch (e) {
          console.error('[set-item-modal]', e);
          this.classList.remove(this.classesToRemoveOnLoad);
          target.innerHTML = '<p class="set-item-qv__error">İçerik yüklenemedi.</p>';
        }
      }

      /**
       * innerHTML does not execute <script>, and the A+ block depends on two of
       * them (a lazy-image loader and a height fixer). Re-creating each tag is how
       * quick-view.js solves the same problem; this is that method plus a fallback,
       * because those images are authored at opacity 0 and stay INVISIBLE rather
       * than merely unloaded if the loader fails to run.
       */
      setInnerHTML(element, fragment) {
        element.innerHTML = '';
        element.appendChild(fragment);

        element.querySelectorAll('script').forEach((oldScript) => {
          const s = document.createElement('script');
          Array.from(oldScript.attributes).forEach((a) => s.setAttribute(a.name, a.value));
          s.appendChild(document.createTextNode(oldScript.innerHTML));
          oldScript.parentNode.replaceChild(s, oldScript);
        });

        // Fallback: if the external loader has not revealed the images shortly
        // after injection, promote data-src ourselves so the panel is never blank.
        setTimeout(() => {
          element.querySelectorAll('img[data-src]').forEach((img) => {
            if (!img.getAttribute('src')) img.src = img.dataset.src;
            if (parseFloat(getComputedStyle(img).opacity) === 0) img.style.opacity = '1';
          });
        }, 1200);
      }
    }
  );
}

/**
 * One delegated listener for the whole document. Bound at capture-free bubble
 * phase on click, so it keeps working across every grid re-render.
 */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-set-item]');
  if (!trigger) return;

  const modal = document.getElementById('SetItemModal');
  if (!modal) return;

  event.preventDefault();
  modal.handle = trigger.dataset.setItem;

  // A click inside the rail is a switch within the set the chrome already
  // describes — rebuilding it there would replace the rail underneath the
  // pointer. Only a click on a card's strip establishes a new set.
  const strip = trigger.closest('.set-strip');
  if (strip) modal.buildChrome(strip);

  if (modal.open) {
    // Already open on a different component: swap the body in place rather than
    // closing and reopening, which would flash the overlay.
    modal.classList.add('drawer--loading');
    modal.syncRail();
    modal.load();
  } else {
    modal.show(trigger);
  }
});
